<template>
  <div class="">
    <div class="panel-heading">
      <!-- selected class name -->
      <div v-if="currentClass" class="has-text-left is-size-7">
        <p>{{ currentClass.schoolName }}</p>
        <p>{{ currentClass.groupName }}</p>
      </div>
      <span v-else> No class selected </span>

      <div class="panel-heading-buttons">
        <b-dropdown
          v-if="curriculums.length > 0"
          aria-role="list"
          position="is-bottom-left"
          :triggers="['hover']"
          :hoverable="true"
        >
          <template #trigger>
            <ButtonTooltip
              size="is-small"
              class="filter-button"
              tooltip="Initialize lectures from curriculum"
              position="is-top"
              type="is-primary"
              icon-left="calendar-plus"
              label="Apply"
            />
          </template>

          <b-dropdown-item
            v-for="curriculum in curriculums"
            :key="curriculum.id"
            aria-role="listitem"
            @click="initializeLecturesFromCurriculum(curriculum)"
          >
            📖 {{ curriculum.title }}
          </b-dropdown-item>
        </b-dropdown>

        <ButtonTooltip
          size="is-small"
          class="filter-button"
          tooltip="Save lectures as a new curriculum"
          position="is-top"
          :delay="300"
          type="is-warning"
          icon-left="save"
          label="Save"
          :disabled="!lectures.length"
          @click="openSaveCurriculumModal"
        />
      </div>
    </div>

    <div class="column-container">
      <div class="items-list" v-if="lectures.length">
        <ModulePlaceholder
          v-for="(lecture, index) in lectures"
          title="Lecture"
          :key="lecture.id"
          :index="index"
          :topics="lecture.topicIds.map(lessonObj)"
          :canEdit="false"
          :canRemove="false"
          @drop="canEdit && $emit('drop', $event)"
          @full="$emit('full', { index })"
          @edit="canEdit && $emit('edit', lecture)"
          @clear="canEdit && $emit('clear', lecture.id)"
          @select-topic="$emit('select-topic', $event)"
          @remove-topic="canEdit && $emit('remove-topic', $event)"
        >
          <template #header>
            <div
              class="is-flex is-justify-content-space-between is-align-items-center w-100"
            >
              <div>
                <span class="week-number">{{ index + 1 }}.</span>
                <!-- date -->
                <span class="lecture-date mx-1">{{
                  lecture.lectureDate | asDate
                }}</span>
                <!-- time -->
                <span class="subtitle is-7">{{
                  lecture.lectureDate | asTime
                }}</span>
                <span class="subtitle is-7" v-if="lecture.lectureEndDate">
                  - {{ lecture.lectureEndDate | asTime }}
                </span>
              </div>

              <div>
                <LectureStatusBadge
                  v-if="lecture.status !== 'Created'"
                  :status="lecture.status"
                />
              </div>

              <div>
                <b-button
                  v-if="
                    lecture.status !== 'Completed' && lecture.topicIds.length
                  "
                  type="is-info"
                  size="is-small"
                  icon-left="chalkboard-teacher"
                  @click="goToLecture(lecture.id)"
                >
                  {{ lecture.firstName || "Coach" }} {{ lecture.lastName }}
                </b-button>
              </div>
            </div>
          </template>

          <template #content>
            <span class="has-text-left is-size-7 mb-2">
              {{ lecture.title }}
            </span>
          </template>
        </ModulePlaceholder>
      </div>
      <div v-else class="has-text-centered p-4">No lectures available</div>
    </div>

    <!-- Save Curriculum Modal -->
    <b-modal
      v-model="isSaveCurriculumModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <CurriculumEditor
        :is-editing="false"
        :form-data="curriculumFormData"
        :is-loading="isSaving"
        @submit="saveLecturesAsCurriculum"
        @close="closeSaveCurriculumModal"
      />
    </b-modal>
  </div>
</template>

<script>
import ModulePlaceholder from "../curriculum/ModulePlaceholder.vue";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
import { LectureService, CurriculumService } from "@/services/CoachingServices";
import { Lecture, ContentStatus } from "vue-chess-shared";
import CurriculumEditor from "../curriculum/CurriculumEditor.vue";
import LectureStatusBadge from "./atoms/LectureStatusBadge.vue";
import { mapGetters } from "vuex";

export default {
  name: "LecturesColumn",

  components: {
    ModulePlaceholder,
    ButtonTooltip,
    CurriculumEditor,
    LectureStatusBadge,
  },

  props: {
    curriculum: { type: Object, default: null },
    getTopicById: { type: Function, required: true },
    lectures: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
    curriculums: { type: Array, default: () => [] },
    classroomId: { type: String, required: true },
  },

  data() {
    return {
      isSaveCurriculumModalActive: false,
      curriculumFormData: {
        title: "",
        description: "",
        status: ContentStatus.Draft,
      },
      isSaving: false,
    };
  },

  computed: {
    // use getCurrentClass from mapgetter
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),
  },

  methods: {
    lessonObj(topicId) {
      return {
        ...this.getTopicById(topicId),
        isOptional: this.curriculum?.lessons?.find(
          (lesson) => lesson.topicId === topicId
        )?.isOptional,
        usage: " ",
      };
    },

    goToLecture(lectureId) {
      // Check if we're in teacher context
      const isTeacherContext = this.$route.meta?.teacher ?? false;
      const lecturePath = isTeacherContext
        ? `/teacher/lectures/${lectureId}`
        : `/lectures/${lectureId}`;
      this.$router.push(lecturePath);
    },

    async initializeLecturesFromCurriculum(curriculum) {
      if (!curriculum || !curriculum.modules || !this.classroomId) {
        this.$buefy.toast.open({
          message:
            "Cannot initialize lectures: missing curriculum or classroom",
          type: "is-danger",
        });
        return;
      }

      try {
        // Get existing lectures
        const existingLectures = await LectureService.getByClassId(
          this.classroomId
        );

        // Ask for confirmation if lectures exist
        if (existingLectures.length > 0) {
          this.$buefy.dialog.confirm({
            title: "Initialize Lectures",
            message: `There are already ${existingLectures.length} lectures for this classroom. The curriculum modules will be applied to existing lectures in order, and new lectures will be created if needed. Continue?`,
            confirmText: "Yes, apply curriculum",
            cancelText: "Cancel",
            type: "is-warning",
            onConfirm: () =>
              this.applyModulesToLectures(curriculum, existingLectures),
          });
        } else {
          // No existing lectures, apply modules
          await this.applyModulesToLectures(curriculum, []);
        }
      } catch (error) {
        console.error("Error initializing lectures:", error);
        this.$buefy.toast.open({
          message: `Failed to initialize lectures: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    async applyModulesToLectures(curriculum, existingLectures) {
      try {
        const today = new Date();
        const updatedLectures = [];
        const newLectures = [];

        // Apply modules to lectures in order
        curriculum.modules.forEach((module, index) => {
          // Remove topics if existing in lectures
          this.lectures.forEach((l) => {
            module.topicIds.forEach((topicId) => {
              l.removeTopic(topicId);
            });
          });

          if (index < existingLectures.length) {
            // Update existing lecture
            const lecture = existingLectures[index];
            lecture.topicIds = [...module.topicIds]; // Replace topicIds with module's topicIds
            updatedLectures.push(lecture);
          } else {
            // Create new lecture
            const lectureDate = new Date(today);
            lectureDate.setDate(today.getDate() + index * 7); // One lecture per week

            const newLecture = new Lecture({
              classroomId: this.classroomId,
              topicIds: module.topicIds,
              weekNumber: index + 1,
              lectureDate,
            });
            newLectures.push(newLecture);
          }
        });

        // Update existing lectures
        await Promise.all(
          updatedLectures.map((lecture) =>
            LectureService.updateLecture(lecture.id, lecture)
          )
        );

        // Create new lectures
        await Promise.all(
          newLectures.map((lecture) => LectureService.createLecture(lecture))
        );

        const message = [];
        if (updatedLectures.length > 0) {
          message.push(`Updated ${updatedLectures.length} existing lectures`);
        }
        if (newLectures.length > 0) {
          message.push(`Created ${newLectures.length} new lectures`);
        }

        this.$buefy.toast.open({
          message: `Successfully applied curriculum "${
            curriculum.title
          }": ${message.join(" and ")}`,
          type: "is-success",
        });

        // Emit event to reload lectures
        this.$emit("lecture-updated");
      } catch (error) {
        console.error("Error applying modules to lectures:", error);
        this.$buefy.toast.open({
          message: `Failed to apply curriculum: ${error.message}`,
          type: "is-danger",
        });
      }
    },

    openSaveCurriculumModal() {
      if (!this.lectures.length) {
        this.$buefy.toast.open({
          message: "No lectures to save",
          type: "is-warning",
        });
        return;
      }

      // Generate a default title based on classroom ID
      this.curriculumFormData = {
        title: `Curriculum for ${this.classroomId}`,
        description: `Created from lectures on ${new Date().toLocaleDateString()}`,
        status: ContentStatus.Draft,
      };

      this.isSaveCurriculumModalActive = true;
    },

    async saveLecturesAsCurriculum(formData) {
      if (!formData.title.trim()) {
        this.$buefy.toast.open({
          message: "Please enter a title for the curriculum",
          type: "is-danger",
        });
        return;
      }

      // Update the form data
      this.curriculumFormData = formData;

      try {
        this.isSaving = true;

        // Create modules from lectures
        const modules = this.lectures.map((lecture) => ({
          topicIds: [...lecture.topicIds], // Create a copy of the array
        }));

        // Create lessons from all unique topics
        const uniqueTopicIds = new Set();
        this.lectures.forEach((lecture) => {
          lecture.topicIds.forEach((topicId) => {
            uniqueTopicIds.add(topicId);
          });
        });

        const lessons = Array.from(uniqueTopicIds).map((topicId) => ({
          topicId,
          isOptional: false,
        }));

        // Create the curriculum
        const curriculum = await CurriculumService.createCurriculum({
          title: this.curriculumFormData.title.trim(),
          description: this.curriculumFormData.description.trim(),
          status: this.curriculumFormData.status,
          modules,
          lessons,
        });

        this.$buefy.toast.open({
          message: `Successfully created curriculum "${curriculum.title}" with ${modules.length} modules`,
          type: "is-success",
        });

        this.isSaveCurriculumModalActive = false;

        // Emit event to reload curriculums
        this.$emit("curriculum-created", curriculum.id);
      } catch (error) {
        console.error("Error saving curriculum:", error);
        this.$buefy.toast.open({
          message: `Failed to save curriculum: ${error.message}`,
          type: "is-danger",
        });
      } finally {
        this.isSaving = false;
      }
    },

    closeSaveCurriculumModal() {
      this.isSaveCurriculumModalActive = false;
      this.curriculumFormData = {
        title: "",
        description: "",
        status: ContentStatus.Draft,
      };
    },
  },
};
</script>

<style scoped>
.panel-heading {
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  border-radius: 0;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.panel-heading-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.column-container {
  height: calc(100% - 52px); /* Adjusted to account for the panel heading */
  overflow-y: auto;
}

.items-list {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.week-number {
  font-weight: bold;
  align-self: start;
}

.lecture-date {
  align-self: flex-start;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.topics-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.w-100 {
  width: 100%;
}
</style>
